import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getCaptchaChallengeApi } from "../../api/captchaApi";
import { sendCustomerSupportRequestApi } from "../../api/customerSupportApi";
import SimpleCaptcha, { createCaptchaChallenge, type CaptchaChallenge } from "../SimpleCaptcha/SimpleCaptcha";
import type { RootState } from "../../store/store";
import styles from "./CustomerSupportWidget.module.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CustomerSupportWidget: React.FC = () => {
  const { t } = useTranslation();
  const authUser = useSelector((state: RootState) => state.auth.user as any);
  const connectedEmail = typeof authUser?.email === "string" ? authUser.email.trim() : "";
  const isConnected = Boolean(connectedEmail);
  const [isOpen, setIsOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [subjectError, setSubjectError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [captchaChallenge, setCaptchaChallenge] = useState<CaptchaChallenge>(createCaptchaChallenge);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const refreshCaptcha = useCallback(async (errorMessage = "") => {
    setCaptchaInput("");
    setCaptchaError(errorMessage);
    try {
      const challenge = await getCaptchaChallengeApi();
      setCaptchaChallenge(challenge);
    } catch (_error) {
      setCaptchaChallenge(createCaptchaChallenge());
      setCaptchaError(errorMessage || t("customerSupport.captchaLoadError"));
    }
  }, [t]);

  useEffect(() => {
    if (!isOpen) return;
    void refreshCaptcha();
  }, [isOpen, refreshCaptcha]);

  const closePopup = () => {
    setIsOpen(false);
    setSubjectError("");
    setEmailError("");
    setDescriptionError("");
    setCaptchaError("");
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    const trimmedSubject = subject.trim();
    const trimmedEmail = email.trim();
    const trimmedDescription = description.trim();
    const trimmedCaptcha = captchaInput.trim();
    const nextSubjectError = trimmedSubject ? "" : t("customerSupport.subjectRequired");
    const nextEmailError = isConnected || EMAIL_RE.test(trimmedEmail)
      ? ""
      : t("customerSupport.emailInvalid");
    const nextDescriptionError = trimmedDescription ? "" : t("customerSupport.descriptionRequired");
    const nextCaptchaError = !trimmedCaptcha
      ? t("captcha.errorRequired")
      : !captchaChallenge.proof
        ? t("customerSupport.captchaValidateError")
        : trimmedCaptcha.toUpperCase() !== captchaChallenge.code.toUpperCase()
          ? t("captcha.errorInvalid")
          : "";

    setSubjectError(nextSubjectError);
    setEmailError(nextEmailError);
    setDescriptionError(nextDescriptionError);
    setCaptchaError(nextCaptchaError);

    if (nextSubjectError || nextEmailError || nextDescriptionError || nextCaptchaError) {
      if (nextCaptchaError) void refreshCaptcha(nextCaptchaError);
      return;
    }

    try {
      setIsSubmitting(true);
      await sendCustomerSupportRequestApi({
        subject: trimmedSubject,
        description: trimmedDescription,
        ...(isConnected ? {} : { email: trimmedEmail }),
        captcha: {
          input: trimmedCaptcha,
          code: captchaChallenge.code,
          proof: String(captchaChallenge.proof || ""),
        },
      });
      toast.success(t("customerSupport.sendSuccess"));
      setSubject("");
      setEmail("");
      setDescription("");
      await refreshCaptcha();
      closePopup();
    } catch (error: any) {
      const message = error?.response?.data?.msg || t("customerSupport.sendError");
      if (String(error?.response?.data?.code || "") === "CAPTCHA_INVALID") {
        setCaptchaError(t("captcha.errorInvalid"));
        void refreshCaptcha(t("captcha.errorInvalid"));
      }
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={styles.floatingButton}
        onClick={() => setIsOpen(true)}
        aria-label={t("customerSupport.openAria")}
      >
        ?
      </button>

      {isOpen && (
        <div className={styles.overlay} onClick={closePopup}>
          <section
            className={styles.popup}
            aria-modal="true"
            role="dialog"
            aria-labelledby="customer-support-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className={styles.closeButton}
              onClick={closePopup}
              aria-label={t("customerSupport.closeAria")}
            >
              x
            </button>

            <h2 id="customer-support-title" className={styles.title}>
              {t("customerSupport.title")}
            </h2>
            <p className={styles.subtitle}>{t("customerSupport.subtitle")}</p>

            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.field}>
                <label htmlFor="customer-support-subject">{t("customerSupport.subjectLabel")}</label>
                <input
                  id="customer-support-subject"
                  type="text"
                  value={subject}
                  onChange={(event) => {
                    setSubject(event.target.value);
                    if (subjectError) setSubjectError("");
                  }}
                  placeholder={t("customerSupport.subjectPlaceholder")}
                />
                {subjectError && <span className={styles.error}>{subjectError}</span>}
              </div>

              {!isConnected && (
                <div className={styles.field}>
                  <label htmlFor="customer-support-email">{t("customerSupport.emailLabel")}</label>
                  <input
                    id="customer-support-email"
                    type="email"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      if (emailError) setEmailError("");
                    }}
                    placeholder={t("customerSupport.emailPlaceholder")}
                    autoComplete="email"
                  />
                  {emailError && <span className={styles.error}>{emailError}</span>}
                </div>
              )}

              <div className={styles.field}>
                <label htmlFor="customer-support-description">{t("customerSupport.descriptionLabel")}</label>
                <textarea
                  id="customer-support-description"
                  value={description}
                  onChange={(event) => {
                    setDescription(event.target.value);
                    if (descriptionError) setDescriptionError("");
                  }}
                  placeholder={t("customerSupport.descriptionPlaceholder")}
                  rows={5}
                />
                {descriptionError && <span className={styles.error}>{descriptionError}</span>}
              </div>

              <SimpleCaptcha
                challenge={captchaChallenge}
                value={captchaInput}
                onChange={(value) => {
                  setCaptchaInput(value);
                  if (captchaError) setCaptchaError("");
                }}
                onRefresh={() => {
                  void refreshCaptcha();
                }}
                label={t("captcha.label")}
                placeholder={t("customerSupport.captchaPlaceholder")}
                refreshLabel={t("captcha.refresh")}
                error={captchaError}
                idPrefix="customer-support"
                layout="inline"
              />

              <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
                {isSubmitting ? t("customerSupport.sending") : t("customerSupport.submit")}
              </button>
            </form>
          </section>
        </div>
      )}
    </>
  );
};

export default CustomerSupportWidget;
